import WPBeanWCProductGallery from "./wc-product-gallery/wc-product-gallery.js";

/**
 * Default zoom options — used as a baseline whenever no saved rule matches
 * the current product, or when a saved rule omits a particular key.
 * Keep in sync with DEFAULT_SETTINGS in constants/defaultSettings.js.
 */
const DEFAULTS = {
    variationImageChange: true,
    variationGallery: true,
    preloadImages: true,

    zoomType: "inner",
    zoomLevel: 2.5,
    lensSize: 130,
    thumbnailPosition: "bottom",
    thumbnailLayout: "slider",

    // Lightbox popup options
    lightboxOnClick: true,
    lightboxOpenOnImageClick: true,
    lightboxCloseOnOverlay: true,
    lightboxLoop: true,
    lightboxKeyboard: true,
    lightboxZoomIn: true,
    lightboxFullscreen: true,
    lightboxAutoplay: false,
    lightboxAutoplayDelay: 3500,

    zoomEasing: true,
    touchZoom: true,
};

function _wcpgInit() {
    if (typeof WPBeanWCProductGallery === "undefined") {
        return;
    }

    const gallery = document.querySelector(".wpbean-product-gallery");
    if (!gallery) return;

    // Read the active zoom config from the data attribute written by
    // class-wc-gallery-loader.php. This approach works regardless of which
    // caching, minification, or script-optimisation plugin is installed:
    // data attributes are plain HTML and are never touched by JS optimisers.
    let savedConfig = {};
    const raw = gallery.dataset.wcpgConfig;
    if (raw) {
        try {
            savedConfig = JSON.parse(raw);
        } catch (e) {
            // Malformed JSON — fall back to defaults silently
        }
    }

    // Saved settings override defaults; any key not in savedConfig keeps its default.
    const options = { ...DEFAULTS, ...savedConfig };

    const wcpgInstance = WPBeanWCProductGallery.init(
        ".wpbean-product-gallery",
        options,
    );

    // ── WooCommerce variation image change (Passive Bridge) ────────────────
    // Only wire up listeners when the "Variation Image Change" option is enabled.
    if (wcpgInstance && options.variationImageChange) {
        // Track whether the current variation triggered a full gallery swap so
        // the correct reset method is called on reset_data.
        let _isVariationGallery = false;

        const handleVariation = (variation) => {
            if (!variation || !variation.image || !variation.image.src) return;

            // ── Variation Gallery (multiple images) ───────────────────────
            // If the variation ships a full gallery array (injected by PHP when
            // the variationGallery setting is enabled), replace the entire gallery.
            if (
                options.variationGallery &&
                Array.isArray(variation.wcpg_variation_gallery) &&
                variation.wcpg_variation_gallery.length > 0
            ) {
                _isVariationGallery = true;
                wcpgInstance.swapVariationGallery(
                    variation.wcpg_variation_gallery,
                );
                return;
            }

            // ── Single variation image swap (original behaviour) ───────────
            _isVariationGallery = false;

            // Skip if the variation has no dedicated image. wcpg_has_image is
            // injected by PHP (false = no image). Treat missing flag + no
            // wcpg_main_src as the same: variation falls back to parent image.
            const hasVariationImage =
                variation.image.wcpg_has_image === true ||
                !!variation.image.wcpg_main_src;
            if (!hasVariationImage) {
                wcpgInstance.resetVariationImage();
                return;
            }

            // Use the correctly-sized URLs injected by inject_variation_image_sizes()
            // in class-wc-gallery-loader.php (respects mainImageSize / thumbnailImageSize
            // settings). Falls back to WooCommerce's default woocommerce_single size.
            const displaySrc =
                variation.image.wcpg_main_src || variation.image.src;
            const thumbSrc =
                variation.image.wcpg_thumb_src || variation.image.src;

            wcpgInstance.swapVariationImage(
                displaySrc,
                variation.image.full_src || displaySrc,
                thumbSrc,
            );
        };

        const handleReset = () => {
            if (_isVariationGallery) {
                wcpgInstance.resetVariationGallery();
                _isVariationGallery = false;
            } else {
                wcpgInstance.resetVariationImage();
            }
        };

        // Use jQuery only if it's available on the window
        if (window.jQuery) {
            window
                .jQuery(document)
                .on(
                    "found_variation",
                    "form.variations_form",
                    (event, variation) => {
                        handleVariation(variation);
                    },
                );

            window
                .jQuery(document)
                .on("reset_data", "form.variations_form", () => {
                    handleReset();
                });
        } else {
            /**
             * FALLBACK: MutationObserver
             * If jQuery is somehow absent, we watch for the src change on the main image.
             */
            const mainImg = document.querySelector(".wcpg-main-img");
            if (mainImg) {
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.attributeName === "src") {
                            wcpgInstance.swapVariationImage(
                                mainImg.src,
                                mainImg.src,
                            );
                        }
                    });
                });
                observer.observe(mainImg, { attributes: true });
            }
        }
    }
}

// ── Initialisation ──────────────────────────────────────────────────────────
//
// Three-phase strategy to handle page builders (Divi Theme Builder, Elementor, …):
//
// Phase 1 — early attempt:
//   Builders often defer or async-load third-party scripts so DOMContentLoaded
//   has already fired by the time our bundle executes. The readyState check lets
//   us run immediately in that case rather than waiting indefinitely.
//
// Phase 2 — window.load retry:
//   Builders scan their own module containers for known selectors and may
//   re-initialise those elements with their own gallery logic, overwriting our
//   event bindings. A retry at window.load catches galleries that failed to init
//   during Phase 1 (wcpg-wrapper not yet added).
//
// Phase 3 — Divi et_pb_after_init_modules:
//   Divi's frontend JS fires this jQuery event after it finishes initialising
//   all modules. At that point Divi may have destroyed our event bindings even
//   though _buildDOM() already ran (and wcpg-wrapper is present). We force a
//   full reinit unconditionally so our zoom/lightbox/nav listeners are restored.
//   This is safe because reinit() tears down the old instance cleanly first.

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", _wcpgInit);
} else {
    _wcpgInit();
}

window.addEventListener("load", function () {
    const gallery = document.querySelector(".wpbean-product-gallery");
    // Re-init only when the gallery exists but has NOT been successfully
    // initialised yet (wcpg-wrapper is set by _buildDOM on success).
    if (gallery && !gallery.classList.contains("wcpg-wrapper")) {
        _wcpgInit();
    }
});

// ── Divi Theme Builder: post-module-init reinit ─────────────────────────────
// Divi fires et_pb_after_init_modules once its frontend module init completes.
// By that time Divi may have re-scanned the page, found our gallery (or the WC
// gallery wrapper), and run its own initialisation — wiping our event listeners.
// Calling reinit here restores them regardless of wcpg-wrapper presence.
if (window.jQuery) {
    window.jQuery(document).on("et_pb_after_init_modules", function () {
        const gallery = document.querySelector(".wpbean-product-gallery");
        if (!gallery) return;

        if (gallery._wcpg) {
            // Instance exists — reinit to restore destroyed event bindings.
            gallery._wcpg.reinit();
        } else {
            // Not yet initialised at all — run full init.
            _wcpgInit();
        }
    });
}
